import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Spinner, Input, Textarea, Select,
  makeStyles, mergeClasses, tokens, MessageBar, MessageBarBody,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  Combobox, Option,
} from '@fluentui/react-components'
import {
  ArrowLeft24Regular, Save24Regular, Delete24Regular, DocumentCopy24Regular,
  ChevronUp20Regular, ChevronDown20Regular, Add24Regular,
} from '@fluentui/react-icons'
import {
  pensionContributionsService,
  SavePensionContributionRequest,
  ContributionFrequency,
  ContributionType,
  PensionContributionListItem,
} from '../../services/pensionContributionsService'
import { employeesService } from '../../services/employeesService'
import { NativeDateInput } from '../../components/NativeDateInput'
import { useAuth } from '../../auth/useAuth'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'

const useStyles = makeStyles({
  pageHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    boxShadow: tokens.shadow4,
    marginBottom: '16px',
  },
  changedStripe: { height: '3px', backgroundColor: tokens.colorBrandBackground, borderRadius: '12px 12px 0 0' },
  cardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 24px', cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground2 },
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cardTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  fieldRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    borderBottom: `1px solid ${tokens.colorNeutralBackground3}`,
  },
  fieldRowFull: { gridTemplateColumns: '1fr' },
  fieldCell: { padding: '10px 24px 12px', display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldCellBorderRight: { borderRight: `1px solid ${tokens.colorNeutralBackground3}` },
  fieldLabel: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.5px',
    display: 'flex', alignItems: 'center', gap: '5px',
  },
  fieldLabelChanged: { color: tokens.colorBrandForeground1 },
  changedDot: {
    width: '6px', height: '6px', borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground, flexShrink: 0,
  },
  hint: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  floatingBar: {
    position: 'sticky', bottom: '16px',
    display: 'flex', alignItems: 'center', gap: '10px',
    backgroundColor: '#1f1f1f', borderRadius: '12px',
    padding: '12px 20px',
    boxShadow: '0 8px 32px rgba(0,0,0,.25)',
    marginTop: '8px', zIndex: 10,
  },
  floatingHint: { fontSize: '13px', color: 'rgba(255,255,255,.6)', marginRight: 'auto' },
})

interface FormState {
  employeeId: string
  employeeName: string
  pensionProvider: string
  accountNumber: string
  variableSymbol: string
  specificSymbol: string
  amount: string
  frequency: string
  contributionType: string
  validFrom: string
  validTo: string
  note: string
}

const emptyForm: FormState = {
  employeeId: '', employeeName: '',
  pensionProvider: '', accountNumber: '',
  variableSymbol: '', specificSymbol: '',
  amount: '', frequency: '1', contributionType: '1',
  validFrom: '', validTo: '', note: '',
}

export default function PensionContributionFormPage() {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  // Kopie z neaktivního záznamu (předáno přes navigate state)
  const copyFrom = (location.state as { copyFrom?: PensionContributionListItem } | null)?.copyFrom
  const qc = useQueryClient()
  const { isHR, isAdmin } = useAuth()

  const [form, setForm] = useState<FormState>(emptyForm)
  const parseDecimal = (s: string) => Number(s.replace(',', '.'))

  const [open, setOpen] = useState(true)
  const [deleteDialog, setDeleteDialog] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [showNewDialog, setShowNewDialog] = useState(false)

  // Unsaved changes tracking
  const savedFormRef = useRef<FormState>(emptyForm)
  const isDirtyRef = useRef(false)
  isDirtyRef.current = JSON.stringify(form) !== JSON.stringify(savedFormRef.current)
  const blocker = useUnsavedChanges(isDirtyRef)

  // Načtení existujícího záznamu
  const { data: detail, isLoading } = useQuery({
    queryKey: ['pension-contribution', id],
    queryFn: () => pensionContributionsService.getById(Number(id)),
    enabled: isEdit,
  })

  // Zaměstnanci pro autocomplete
  const { data: employeesData } = useQuery({
    queryKey: ['employees-active'],
    queryFn: () => employeesService.getAll({ pageSize: 500, status: 1 }),
  })
  const employees = employeesData?.items ?? []

  // Předvyplnění z kopie (pouze při novém záznamu)
  useEffect(() => {
    if (!isEdit && copyFrom) {
      const f: FormState = {
        employeeId: String(copyFrom.employeeId),
        employeeName: copyFrom.employeeFullName,
        pensionProvider: copyFrom.pensionProvider,
        accountNumber: '',
        variableSymbol: '',
        specificSymbol: '',
        amount: String(copyFrom.amount),
        frequency: String(copyFrom.frequency),
        contributionType: String(copyFrom.contributionType),
        validFrom: '', validTo: '', note: '',
      }
      setForm(f)
      savedFormRef.current = f
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (detail) {
      const f: FormState = {
        employeeId: String(detail.employeeId),
        employeeName: detail.employeeFullName,
        pensionProvider: detail.pensionProvider,
        accountNumber: detail.accountNumber,
        variableSymbol: detail.variableSymbol,
        specificSymbol: detail.specificSymbol,
        amount: String(detail.amount).replace('.', ','),
        frequency: String(detail.frequency),
        contributionType: String(detail.contributionType),
        validFrom: detail.validFrom?.slice(0, 10) ?? '',
        validTo: detail.validTo?.slice(0, 10) ?? '',
        note: detail.note ?? '',
      }
      setForm(f)
      savedFormRef.current = f
    }
  }, [detail])

  const set = (key: keyof FormState) => (_: unknown, d: { value: string }) =>
    setForm(f => ({ ...f, [key]: d.value }))

  const changed = (key: keyof FormState) =>
    form[key] !== savedFormRef.current[key]

  function cellLabel(label: string, key: keyof FormState) {
    const isChanged = changed(key)
    return (
      <span className={mergeClasses(styles.fieldLabel, isChanged ? styles.fieldLabelChanged : undefined)}>
        {isChanged && <span className={styles.changedDot} />}
        {label}
      </span>
    )
  }

  function toRequest(): SavePensionContributionRequest {
    return {
      employeeId: Number(form.employeeId),
      pensionProvider: form.pensionProvider,
      accountNumber: form.accountNumber,
      variableSymbol: form.variableSymbol,
      specificSymbol: form.specificSymbol,
      amount: parseDecimal(form.amount) || 0,
      frequency: Number(form.frequency) as ContributionFrequency,
      contributionType: Number(form.contributionType) as ContributionType,
      validFrom: form.validFrom || null,
      validTo: form.validTo || null,
      note: form.note || null,
    }
  }

  const saveMutation = useMutation({
    mutationFn: () => isEdit
      ? pensionContributionsService.update(Number(id), toRequest()).then(() => ({ id: Number(id) }))
      : pensionContributionsService.create(toRequest()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pension-contributions'] })
      qc.invalidateQueries({ queryKey: ['pension-contribution', id] })
      isDirtyRef.current = false
      savedFormRef.current = form
      setServerError(null)
      // Pokud je vyplněno ValidTo → nabídnout nový záznam
      if (form.validTo) {
        setShowNewDialog(true)
      } else {
        navigate('/pension-contributions')
      }
    },
    onError: (err: Error) => setServerError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: () => pensionContributionsService.delete(Number(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pension-contributions'] })
      isDirtyRef.current = false
      navigate('/pension-contributions')
    },
  })

  const handleNewRecord = () => {
    // Nakopíruj hodnoty, vymaž ValidFrom a ValidTo
    const newForm: FormState = { ...form, validFrom: '', validTo: '', employeeId: form.employeeId, employeeName: form.employeeName }
    setForm(newForm)
    savedFormRef.current = newForm
    setShowNewDialog(false)
    navigate('/pension-contributions/new', { replace: true })
  }

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedFormRef.current)

  if (isLoading) return <Spinner label="Načítám…" />

  return (
    <div>
      <UnsavedChangesDialog blocker={blocker} />

      {/* Hlavička */}
      <div className={styles.pageHeader}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/pension-contributions')} />
        <Text size={600} weight="semibold">
          {isEdit ? 'Upravit příspěvek na stáří' : 'Nový příspěvek na stáří'}
        </Text>
      </div>

      {serverError && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{serverError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Karta — Příspěvek */}
      <div className={styles.card}>
        <div className={styles.cardHeader} onClick={() => setOpen(o => !o)}>
          <span className={styles.cardTitle}>Příspěvek na stáří</span>
          {open ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>

        {open && (
          <>
            {/* Zaměstnanec */}
            <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
              <div className={styles.fieldCell}>
                {cellLabel('Zaměstnanec', 'employeeName')}
                <Combobox
                  freeform
                  placeholder="— vyhledejte zaměstnance —"
                  value={form.employeeName}
                  onChange={e => setForm(f => ({ ...f, employeeName: e.target.value, employeeId: '' }))}
                  onOptionSelect={(_, d) => {
                    const emp = employees.find(e => String(e.id) === d.optionValue)
                    if (emp) setForm(f => ({ ...f, employeeId: String(emp.id), employeeName: emp.fullName }))
                  }}
                  disabled={isEdit}
                >
                  {employees.map(e => (
                    <Option key={e.id} value={String(e.id)} text={e.fullName}>
                      {e.fullName}
                      {e.departmentName && <span style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}> · {e.departmentName}</span>}
                    </Option>
                  ))}
                </Combobox>
                {isEdit && <span className={styles.hint}>Zaměstnance nelze změnit po vytvoření záznamu</span>}
              </div>
            </div>

            {/* Penzijní ústav | Číslo účtu */}
            <div className={styles.fieldRow}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                {cellLabel('Penzijní ústav / správce DIP', 'pensionProvider')}
                <Input value={form.pensionProvider} onChange={set('pensionProvider')} />
              </div>
              <div className={styles.fieldCell}>
                {cellLabel('Číslo účtu', 'accountNumber')}
                <Input value={form.accountNumber} onChange={set('accountNumber')} />
              </div>
            </div>

            {/* VS | SS */}
            <div className={styles.fieldRow}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                {cellLabel('Variabilní symbol', 'variableSymbol')}
                <Input value={form.variableSymbol} onChange={set('variableSymbol')} />
              </div>
              <div className={styles.fieldCell}>
                {cellLabel('Specifický symbol', 'specificSymbol')}
                <Input value={form.specificSymbol} onChange={set('specificSymbol')} />
              </div>
            </div>

            {/* Výše | Frekvence */}
            <div className={styles.fieldRow}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                {cellLabel('Výše příspěvku (Kč)', 'amount')}
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={e => {
                    const raw = (e.target?.value ?? '').replace(/[^0-9.,]/g, '')
                    setForm(f => ({ ...f, amount: raw }))
                  }}
                  contentAfter={<span style={{ color: tokens.colorNeutralForeground3 }}>Kč</span>}
                />
              </div>
              <div className={styles.fieldCell}>
                {cellLabel('Frekvence', 'frequency')}
                <Select value={form.frequency} onChange={(_, d) => setForm(f => ({ ...f, frequency: d.value }))}>
                  <option value="1">Měsíčně</option>
                  <option value="2">Čtvrtletně</option>
                  <option value="3">Ročně</option>
                </Select>
              </div>
            </div>

            {/* Typ | prázdné */}
            <div className={styles.fieldRow}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                {cellLabel('Typ příspěvku', 'contributionType')}
                <Select value={form.contributionType} onChange={(_, d) => setForm(f => ({ ...f, contributionType: d.value }))}>
                  <option value="1">Zaměstnanec</option>
                  <option value="2">ORBIT</option>
                </Select>
              </div>
              <div className={styles.fieldCell} />
            </div>

            {/* Zahájení | Ukončení */}
            <div className={styles.fieldRow}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                {cellLabel('Zahájení výplaty', 'validFrom')}
                <NativeDateInput value={form.validFrom} onChange={v => setForm(f => ({ ...f, validFrom: v }))} />
              </div>
              <div className={styles.fieldCell}>
                {cellLabel('Ukončení výplaty', 'validTo')}
                <NativeDateInput value={form.validTo} onChange={v => setForm(f => ({ ...f, validTo: v }))} min={form.validFrom || undefined} />
                <span className={styles.hint}>Vyplňte při ukončení platnosti záznamu</span>
              </div>
            </div>

            {/* Poznámka */}
            <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)} style={{ borderBottom: 'none' }}>
              <div className={styles.fieldCell}>
                {cellLabel('Poznámka', 'note')}
                <Textarea value={form.note} onChange={(_, d) => setForm(f => ({ ...f, note: d.value }))} rows={3} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Floating bar */}
      <div className={styles.floatingBar}>
        {isEdit && (isHR || isAdmin) && detail?.validTo && detail.validTo < new Date().toISOString().slice(0, 10) && (
          <Button
            appearance="subtle"
            icon={<Delete24Regular />}
            type="button"
            style={{ color: '#f87171' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={() => setDeleteDialog(true)}
          >
            Trvale odstranit
          </Button>
        )}
        <span className={styles.floatingHint}>{isDirty ? '✏ Neuložené změny' : ''}</span>
        {isEdit && detail?.validTo && detail.validTo < new Date().toISOString().slice(0, 10) && (
          <Button
            appearance="subtle"
            icon={<DocumentCopy24Regular />}
            type="button"
            style={{ color: 'rgba(255,255,255,0.8)' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={() => navigate('/pension-contributions/new', { state: { copyFrom: detail } })}
          >
            Kopírovat záznam
          </Button>
        )}
        <Button
          appearance="subtle"
          type="button"
          style={{ color: 'rgba(255,255,255,0.8)' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          onClick={() => navigate('/pension-contributions')}
        >
          Zrušit editaci
        </Button>
        <Button
          appearance="primary"
          icon={<Save24Regular />}
          onClick={() => {
            if (form.validTo && form.validFrom && form.validTo < form.validFrom) {
              setServerError('Datum ukončení nemůže být dříve než datum zahájení.')
              return
            }
            setServerError(null)
            saveMutation.mutate()
          }}
          disabled={saveMutation.isPending || !form.employeeId}
        >
          {saveMutation.isPending ? 'Ukládám…' : 'Uložit'}
        </Button>
      </div>

      {/* Dialog — smazat */}
      <Dialog open={deleteDialog} onOpenChange={() => setDeleteDialog(false)}>
        <DialogSurface>
          <DialogTitle>Smazat záznam?</DialogTitle>
          <DialogBody>
            <DialogContent>Opravdu chcete trvale smazat tento příspěvek? Akci nelze vrátit zpět.</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialog(false)}>Zrušit</Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Mazání…' : 'Smazat'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Dialog — nabídnout nový záznam po uložení s ValidTo */}
      <Dialog open={showNewDialog} onOpenChange={() => { setShowNewDialog(false); navigate('/pension-contributions') }}>
        <DialogSurface>
          <DialogTitle>Záznam uložen</DialogTitle>
          <DialogBody>
            <DialogContent>
              Záznam byl uložen s datem ukončení. Chcete rovnou založit nový navazující záznam pro tohoto zaměstnance?
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setShowNewDialog(false); navigate('/pension-contributions') }}>
                Ne, zpět na přehled
              </Button>
              <Button appearance="primary" icon={<Add24Regular />} onClick={handleNewRecord}>
                Ano, nový záznam
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
